import { useContext, useCallback, useRef, useEffect } from 'react';
import { WalkDataContext } from '../hooks/walkDataContext';
import { validateStyleItem, generateStyleId } from './StyleMerger';

/**
 * Hilfsfunktion zum Konvertieren von Strings in gültiges JSON
 * Macht Keys in Anführungszeichen und ersetzt einfache durch doppelte Anführungszeichen
 * WICHTIG: Behandelt "css" Properties speziell, da sie verschachtelte Objekte als String enthalten
 */
export const convertToValidJson = (str) => {
  if (!str) return '[]';

  // Step 1: Protect css property content by temporarily replacing it
  const cssMatches = [];

  // Handle double-quoted css property (match content between outermost quotes)
  let tempStr = str.replace(/"css"\s*:\s*"((?:[^"\\]|\\.)*)"/g, (match, cssContent) => {
    cssMatches.push(cssContent);
    return `"css":"__CSS_PLACEHOLDER_${cssMatches.length - 1}__"`;
  });

  // Handle single-quoted css property with nested content
  // Strategy: Find 'css': and then extract until we find the matching closing quote
  // accounting for nested braces and quotes
  const singleQuoteRegex = /'css'\s*:\s*'/g;
  let match;
  while ((match = singleQuoteRegex.exec(tempStr)) !== null) {
    const startPos = match.index + match[0].length;
    let endPos = startPos;
    let braceDepth = 0;
    let inQuote = false;

    // Parse through the string character by character
    for (let i = startPos; i < tempStr.length; i++) {
      const char = tempStr[i];
      const prevChar = i > 0 ? tempStr[i - 1] : '';

      if (char === '{' && !inQuote) {
        braceDepth++;
      } else if (char === '}' && !inQuote) {
        braceDepth--;
      } else if (char === "'" && prevChar !== '\\') {
        if (braceDepth === 0 && !inQuote) {
          // Found the closing quote for the css property
          endPos = i;
          break;
        }
        inQuote = !inQuote;
      }
    }

    if (endPos > startPos) {
      const cssContent = tempStr.substring(startPos, endPos);
      cssMatches.push(cssContent);
      const placeholder = `"css":"__CSS_PLACEHOLDER_${cssMatches.length - 1}__"`;
      tempStr = tempStr.substring(0, match.index) + placeholder + tempStr.substring(endPos + 1);

      // Reset regex lastIndex since we modified the string
      singleQuoteRegex.lastIndex = 0;
    }
  }

  // Step 2: Replace remaining single quotes with double quotes
  tempStr = tempStr.replace(/'/g, '"');

  // Step 3: Fix unquoted keys (e.g., {type: "style"} -> {"type": "style"})
  tempStr = tempStr.replace(/(\{|,)\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');

  // Step 4: Restore css property content with proper escaping
  cssMatches.forEach((cssContent, index) => {
    // Convert single quotes to double quotes in CSS content (for parseCustomStyles compatibility)
    // Then escape the double quotes for JSON string
    const normalizedContent = cssContent.replace(/'/g, '"');
    const escapedContent = normalizedContent.replace(/"/g, '\\"');
    tempStr = tempStr.replace(`"__CSS_PLACEHOLDER_${index}__"`, `"${escapedContent}"`);
  });

  return tempStr;
};

/**
 * Parse JSON-Daten aus String (ggf. mit einfachen Anführungszeichen)
 */
export const parseJsonContent = (contentString) => {
  try {
    const validJsonString = convertToValidJson(contentString);
    return JSON.parse(validJsonString);
  } catch (error) {
    console.error("Error parsing content:", error, "\nOriginal string:", contentString);
    return [];
  }
};

/**
 * Hook zum Aktivieren von Content-Elementen
 */
export const useContentActivator = () => {
  const {
    contentState,
    setElementState,
    setExpandedQuest,
    setExpandedPuzzle,
    quests,
    addActiveStyle
  } = useContext(WalkDataContext);

  // Queue for pending quest/puzzle expansions
  const pendingExpansionsRef = useRef([]);

  /**
   * Process pending quest/puzzle expansions when contentState becomes available
   */
  useEffect(() => {
    if (!contentState || pendingExpansionsRef.current.length === 0) {
      return;
    }

    // Process all pending expansions
    const itemsToProcess = [...pendingExpansionsRef.current];
    pendingExpansionsRef.current = [];

    itemsToProcess.forEach(item => {
      if (item.type === 'quest') {
        const questState = contentState.find(
          stateItem => stateItem.type === 'quest' && stateItem.slug === item.slug
        );
        if (questState && questState.state === 'active') {
          setExpandedQuest(item.slug);
        }
      } else if (item.type === 'puzzle') {
        const puzzleState = contentState.find(
          stateItem => stateItem.type === 'puzzle' && stateItem.slug === item.slug
        );
        if (puzzleState && puzzleState.state === 'active') {
          setExpandedPuzzle(item.slug);
        }
      }
    });
  }, [contentState, setExpandedQuest, setExpandedPuzzle]);

  /**
   * Aktiviert eine Liste von Content-Elementen
   * @param {Array} activationItems - Array von Objekten mit {type, slug, action?}
   * @param {Function} onComplete - Callback nach Abschluss
   */
  const activateContent = useCallback((activationItems = [], onComplete = null) => {
    // Ensure activationItems is always an array
    const itemsArray = Array.isArray(activationItems) ? activationItems : [activationItems];

    if (!itemsArray || itemsArray.length === 0) return;

    // Kopie des contentState für Updates
    const updatedContentState = [...contentState];

    // Zähler für asynchrone Operationen
    let pendingOperations = itemsArray.length;

    // Alle zu aktivierenden Items durchgehen
    itemsArray.forEach((item, index) => {
      // Handle style activation separately
      if (item.type === 'style') {
        handleStyleActivation(item);
        pendingOperations--;
        if (pendingOperations === 0 && onComplete) {
          onComplete();
        }
        return;
      }

      // Handle quest expansion (only set expandedQuest, don't activate)
      if (item.type === 'quest' && item.action === 'open') {
        if (!contentState) {
          // Queue for later processing when contentState is available
          pendingExpansionsRef.current.push(item);
        } else {
          const questState = contentState.find(
            stateItem => stateItem.type === 'quest' && stateItem.slug === item.slug
          );
          if (questState && questState.state === 'active') {
            setExpandedQuest(item.slug);
          }
        }
        pendingOperations--;
        if (pendingOperations === 0 && onComplete) {
          onComplete();
        }
        return;
      }

      // Handle puzzle expansion (only set expandedPuzzle, don't activate)
      if (item.type === 'puzzle' && item.action === 'open') {
        if (!contentState) {
          // Queue for later processing when contentState is available
          pendingExpansionsRef.current.push(item);
        } else {
          const puzzleState = contentState.find(
            stateItem => stateItem.type === 'puzzle' && stateItem.slug === item.slug
          );
          if (puzzleState && puzzleState.state === 'active') {
            setExpandedPuzzle(item.slug);
          }
        }
        pendingOperations--;
        if (pendingOperations === 0 && onComplete) {
          onComplete();
        }
        return;
      }

      // Bestimme den Delay (Standard: 3s für 'track', 0s für andere)
      const delay = item.delay !== undefined
        ? parseFloat(item.delay)
        : (item.type === 'track' ? 0.5 : 0);

      // Funktion zum Aktualisieren des Zustands nach dem Delay
      const updateAfterDelay = () => {
        const index = updatedContentState.findIndex(
          stateItem => stateItem.type === item.type && stateItem.slug === item.slug
        );
        
        // Bestimme den aktuellen Zustand und Highlight-Status
        const currentState = index !== -1 ? updatedContentState[index].state : 'inactive';
        const currentHighlight = index !== -1 ? updatedContentState[index].highlight : false;
        
        // Bestimme den neuen Zustand
        const resolvedAction = item.action === 'solve' ? 'finished' : item.action;
        const newState = resolvedAction || (currentState === 'finished' ? 'finished' : 'active');
        const parsedHighlight = item.highlight === 'true' ? true : item.highlight === 'false' ? false : item.highlight;
        const newHighlightState = parsedHighlight !== undefined ? parsedHighlight : (currentState === 'inactive' ? true : currentHighlight);
        
        // Nur aktualisieren, wenn der state nicht "finished" ist, es sei denn action ist explizit angegeben
        if (currentState !== 'finished' || item.action) {
          setElementState(item.type, item.slug, newState, newHighlightState);
          
          // Lokale Kopie auch aktualisieren
          if (index !== -1) {
            updatedContentState[index] = {
              ...updatedContentState[index],
              state: newState,
              highlight: newHighlightState
            };
          } else {
            updatedContentState.push({
              type: item.type,
              slug: item.slug,
              state: newState,
              highlight: newHighlightState
            });
          }
        }
        
        // Überprüfen, ob alle Operationen abgeschlossen sind
        pendingOperations--;
        if (pendingOperations === 0 && onComplete) {
          onComplete();
        }
      };
      
      // Verzögerung anwenden, wenn nötig
      if (delay > 0) {
        setTimeout(updateAfterDelay, delay * 1000);
      } else {
        updateAfterDelay();
      }
    });
  }, [contentState, addActiveStyle, setExpandedQuest, setExpandedPuzzle]);

  /**
   * Handles style activation (inline CSS only)
   * @param {object} styleItem - Style activation item with {type: 'style', css}
   */
  const handleStyleActivation = (styleItem) => {
    if (!validateStyleItem(styleItem)) {
      return;
    }

    const cssString = styleItem.css;
    // Generate deterministic ID based on CSS content to prevent duplicates
    const styleId = styleItem.id || generateStyleId(cssString);
    const delay = styleItem.delay !== undefined ? parseFloat(styleItem.delay) : 0;

    addActiveStyle(styleId, cssString, delay);
  };

  /**
   * Findet das nächste aktive Puzzle in der Reihenfolge
   */
  const findSmallestActivePuzzle = (quests, contentState) => {
    const sortedQuests = quests.sort((a, b) => a.position - b.position);
  
    for (const quest of sortedQuests) {
      const questState = contentState.find(item => item.type === 'quest' && item.slug === quest.slug);
      
      if (questState && questState.state === 'active') {
        const sortedPuzzles = quest.puzzles.sort((a, b) => a.position - b.position);
        
        for (const puzzle of sortedPuzzles) {
          const puzzleState = contentState.find(item => item.type === 'puzzle' && item.slug === puzzle.slug);
          if (puzzleState && puzzleState.state === 'active') {
            return {
              questSlug: quest.slug,
              puzzleSlug: puzzle.slug
            };
          }
        }
      }
    }
    
    return null;
  };

  /**
   * Aktualisiert die Anzeige auf das nächste aktive Puzzle
   */
  const updateSmallestActivePuzzle = (quests, contentState, setExpandedQuest, setExpandedPuzzle) => {
    const smallest = findSmallestActivePuzzle(quests, contentState);
    if (smallest) {
      setExpandedQuest(smallest.questSlug);
      setExpandedPuzzle(smallest.puzzleSlug);
    }
  };

  return { activateContent, parseJsonContent };
};